import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { services, images } from '../data/mock';
import { Plane, Ship, Container, Warehouse, FileCheck, Truck, ArrowRight } from 'lucide-react';

const iconMap = {
  Plane,
  Ship,
  Container,
  Warehouse,
  FileCheck,
  Truck
};

const Services = () => {
  return (
    <section id="solutions" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-4">
            Our Solutions
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Comprehensive Freight{' '}
            <span className="text-amber-500">Solutions</span>
          </h2>
          <p className="text-lg text-slate-600">
            From air cargo to ocean freight, we provide end-to-end logistics services
            tailored to your business needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <Card
                key={service.id}
                className="group bg-white border-0 shadow-lg shadow-slate-200/50 hover:shadow-xl hover:shadow-slate-200/70 transition-all duration-300 overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-8">
                  {/* Icon */}
                  <div className="w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center mb-6 group-hover:bg-amber-500 transition-colors duration-300">
                    {Icon && <Icon className="w-7 h-7 text-amber-400 group-hover:text-slate-900 transition-colors duration-300" />}
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-amber-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-500">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Link */}
                  <Button
                    variant="ghost"
                    className="p-0 h-auto text-slate-900 font-semibold hover:text-amber-600 hover:bg-transparent group/btn"
                  >
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Feature Image */}
        <div className="mt-20 relative rounded-2xl overflow-hidden">
          <img
            src={images.containers}
            alt="Shipping containers"
            className="w-full h-80 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/60 to-transparent flex items-center">
            <div className="p-8 sm:p-12 max-w-xl">
              <h3 className="text-3xl font-bold text-white mb-4">
                Global Reach, Local Expertise
              </h3>
              <p className="text-slate-300 mb-6">
                With partners in over 50 countries, we combine worldwide logistics capabilities with deep local market knowledge.
              </p>
              <Button className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
                Explore Our Network
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
